import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { showNotification } from 'actions/notification';
import { useGetBots, usePostChannel, usePatchChannel } from 'actions/chatbot';

import {
  FormContainer,
  FormHeader,
  FormTitle,
  Field,
  Label,
  Input,
  FormSelect,
  ButtonRow,
  SaveButton,
  DeleteButton,
  SecondaryButtonRow,
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalText,
  ModalActions,
  ModalCancelButton,
  ModalConfirmDeleteButton,
} from './styled-comps';

function EditForm({ channel, onSaved, isNew = false }) {
  const dispatch = useDispatch();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { get: getBots, data: botsData } = useGetBots();
  const { post: postChannel } = usePostChannel({
    onComplete: errorMessage => {
      setSaving(false);
      if (errorMessage) {
        dispatch(
          showNotification({
            text: 'Failed to save channel',
            duration: 5,
            showCloseIcon: true,
          }),
        );
      } else {
        onSaved && onSaved();
      }
    },
  });
  const { patch: patchChannel } = usePatchChannel({
    onComplete: errorMessage => {
      setSaving(false);
      if (errorMessage) {
        dispatch(
          showNotification({
            text: 'Failed to save channel',
            duration: 5,
            showCloseIcon: true,
          }),
        );
      } else {
        onSaved && onSaved();
      }
    },
  });

  // Admin bots list returns every row (DRAFT + PUBLISHED per bot_key). For the
  // channel picker we want one option per *logical* bot — dedupe on bot_key,
  // preferring PUBLISHED since that's what the channel will actually serve.
  const bots = (() => {
    const raw = Array.isArray(botsData) ? botsData : [];
    const byKey = new Map();
    raw.forEach(b => {
      const existing = byKey.get(b.bot_key);
      if (!existing || (existing.status !== 'PUBLISHED' && b.status === 'PUBLISHED')) {
        byKey.set(b.bot_key, b);
      }
    });
    return Array.from(byKey.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || ''),
    );
  })();

  useEffect(() => {
    getBots('list');
  }, [getBots]);

  useEffect(() => {
    if (channel) {
      setForm({
        name: channel.name || '',
        display_name: channel.display_name || '',
        bot_key: channel.bot_key || '',
        user_type: channel.user_type || '',
      });
    }
  }, [channel]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setSaving(true);
    const payload = {
      name: form.name,
      display_name: form.display_name,
      bot_key: form.bot_key,
      user_type: form.user_type,
    };

    if (isNew) {
      postChannel(payload);
    } else {
      patchChannel(channel.id, payload);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    setSaving(true);
    patchChannel(channel.id, { is_deleted: true });
  };

  if (!channel) return null;

  return (
    <FormContainer>
      <FormHeader>
        <FormTitle>
          {isNew ? 'New Channel' : channel.display_name || channel.name}
        </FormTitle>
      </FormHeader>

      <Field>
        <Label>Name</Label>
        <Input
          value={form.name || ''}
          onChange={e => handleChange('name', e.target.value)}
          placeholder="e.g. CUEMATHAPP"
        />
      </Field>

      <Field>
        <Label>Display Name</Label>
        <Input
          value={form.display_name || ''}
          onChange={e => handleChange('display_name', e.target.value)}
          placeholder="e.g. Cuemath App"
        />
      </Field>

      <Field>
        <Label>Bot</Label>
        <FormSelect
          value={form.bot_key || ''}
          onChange={e => handleChange('bot_key', e.target.value)}
        >
          <option value="">Select a bot...</option>
          {bots.map(bot => (
            <option key={bot.bot_key} value={bot.bot_key}>
              {bot.name}
              {bot.status === 'DRAFT' ? ' (draft only)' : ''}
            </option>
          ))}
        </FormSelect>
      </Field>

      <Field>
        <Label>User Type</Label>
        <FormSelect
          value={form.user_type || ''}
          onChange={e => handleChange('user_type', e.target.value)}
        >
          <option value="">Select user type...</option>
          <option value="PARENT">Parent</option>
          <option value="TEACHER">Teacher</option>
        </FormSelect>
      </Field>

      <ButtonRow>
        <SaveButton
          onClick={handleSave}
          disabled={saving}
          label={saving ? 'Saving...' : isNew ? 'Create Channel' : 'Save'}
        />
      </ButtonRow>

      {!isNew && (
        <SecondaryButtonRow>
          <DeleteButton
            onClick={() => setShowDeleteConfirm(true)}
            disabled={saving}
            label="Delete"
          />
        </SecondaryButtonRow>
      )}

      {showDeleteConfirm && (
        <ModalOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>
              Are you sure you want to delete {channel.display_name || channel.name}?
            </ModalTitle>
            <ModalText>This action cannot be undone.</ModalText>
            <ModalActions>
              <ModalCancelButton
                onClick={() => setShowDeleteConfirm(false)}
                label="Cancel"
              />
              <ModalConfirmDeleteButton label="Delete" onClick={handleDelete} />
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}
    </FormContainer>
  );
}

export default EditForm;
